/**
 * Products browse view - the full LS catalogue, variant children
 * included (SKU search lands on variants; the Type filter narrows).
 * Mirrors the product-setup exception layout: frozen identity columns,
 * per-outlet STOCK columns, pricing + timing bands. Issues roll up the
 * whole product family (setup / pricing / stock, per-outlet tags
 * prefix-matched).
 */
import {
  buildOutletCostColumns,
  buildProductExceptionGroups,
  PRODUCT_EXCEPTION_IDENTITY_COLUMNS,
  PRODUCT_EXCEPTION_PRICING_COLUMNS,
  PRODUCT_EXCEPTION_TIMING_COLUMNS,
} from '../exceptions/shared/productExceptionColumns'
import {
  buildInventoryFilterParams,
  sharedInventoryFilters,
  SHARED_INVENTORY_FILTER_KEYS,
} from '../exceptions/shared/sharedInventoryFilters'
import {
  composeRowSubtitle,
  activePart,
  skuPart,
  textPart,
} from '../exceptions/shared/rowSubtitle'
import {
  formatMainCategory,
  NO_CATEGORY_LABEL,
  NO_CATEGORY_VALUE,
} from '../../utils/productCategory'
import type { ExceptionFilterDef } from '../exceptions/types'
import type { DataExplorerViewConfig, DataViewFacets } from './types'
import {
  buildHasIssuesParam,
  hasIssuesFilter,
  withDetectedAge,
} from './shared'
import inspectorApi from '../../services/inspector/api'

// Shared inventory defs minus the search entry (the page quick-search is
// the serverTable search, hosted by the panel's serverSearch slot) and
// with Main Category switched from extractOptions (page-scoped) to
// static options filled from the whole-catalogue facets.
const baseFilters: ExceptionFilterDef[] = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Name or SKU',
  },
  ...sharedInventoryFilters
    .filter((f) => f.key !== SHARED_INVENTORY_FILTER_KEYS.search)
    .map((f) =>
      f.key === SHARED_INVENTORY_FILTER_KEYS.mainCategory &&
      f.type === 'multiselect'
        ? { ...f, extractOptions: undefined, options: [] }
        : f,
    ),
  hasIssuesFilter,
]

const withCategoryOptions = (
  facets: DataViewFacets | null,
): ExceptionFilterDef[] =>
  baseFilters.map((f) =>
    f.key === SHARED_INVENTORY_FILTER_KEYS.mainCategory &&
    f.type === 'multiselect'
      ? {
          ...f,
          options: (facets?.main_categories ?? []).map((c) => ({
            value: c.value ?? NO_CATEGORY_VALUE,
            label:
              c.value == null
                ? NO_CATEGORY_LABEL
                : formatMainCategory(c.value),
            count: c.count,
            variant: c.value == null ? ('subtle' as const) : undefined,
          })),
        }
      : f,
  )

export const productsViewConfig: DataExplorerViewConfig = {
  viewKey: 'products',
  codes: [],
  fpUserAccessId: 'products',
  pageHeader: {
    title: 'Products',
    description: 'Browse the full Lightspeed catalogue.',
    breadcrumb: 'Products',
  },
  complexity: 'simple',
  rowKeyField: 'ls_id',
  defaultSortColumn: 'name',
  defaultSortDirection: 'asc',
  serverTable: { defaultPageSize: 50, pageSizeOptions: [50, 100], enableSearch: true },
  serverSort: {
    sortableKeys: ['name', 'total_stock', 'price_excluding_tax', 'errors'],
    defaultSortBy: 'name',
    defaultSortDir: 'asc',
  },
  // Wrap the frozen Issues column with the last-flagged-age line (".map"
  // returns a fresh array - the shared identity columns are not mutated,
  // so other product exception tables keep their plain Issues column).
  columns: PRODUCT_EXCEPTION_IDENTITY_COLUMNS.map((c) =>
    c.key === 'errors' ? withDetectedAge(c) : c,
  ),
  columnGroups: buildProductExceptionGroups('stock'),
  disableBulkSelect: true,
  dynamicColumns: (rows, variant, response) => [
    ...buildOutletCostColumns(
      rows,
      variant,
      'stock',
      true,
      response?.outlets as Array<{ id?: string; name?: string }> | undefined,
    ),
    ...PRODUCT_EXCEPTION_PRICING_COLUMNS,
    ...PRODUCT_EXCEPTION_TIMING_COLUMNS,
  ],
  containInCard: true,
  filters: baseFilters,
  resolveFilters: withCategoryOptions,
  buildFetchParams: (urlState) => ({
    ...buildInventoryFilterParams(urlState.filterValues),
    ...buildHasIssuesParam(urlState.filterValues),
  }),
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      skuPart(row.sku),
      textPart(row.category_root_name),
      activePart(row.active),
    ]),
  fetchPage: (fp, params) => inspectorApi.getDataExplorerProducts(fp, params),
  fetchFacets: async (fp) => {
    const facets = await inspectorApi.getDataExplorerProductFacets(fp)
    return { main_categories: facets.main_categories }
  },
}
